import React from 'react';
import { Button, ChevronRightIcon, Chip, cn } from '@billda/design-system';

interface GroupHeaderProps {
  /** Icon component — accepts design-system (Lucide) icons. */
  icon: React.ElementType;
  iconColor?: string;
  label: string;
  count?: number;
  expanded: boolean;
  onToggle: () => void;
  onTextClick?: () => void;
  secondaryText?: React.ReactNode;
}

export const GroupHeader = React.memo(function GroupHeader({
  icon: Icon,
  iconColor,
  label,
  count,
  expanded,
  onToggle,
  onTextClick,
  secondaryText,
}: GroupHeaderProps) {
  const labelContent = (
    <>
      <span className="inline-flex shrink-0 [&_svg]:size-4 [&_svg]:text-content-secondary">
        <Icon style={iconColor ? { color: iconColor } : undefined} />
      </span>
      <span className="type-body-md truncate font-semibold transition-colors duration-150 group-hover/header:text-content-accent">
        {label}
      </span>
    </>
  );

  return (
    <div
      data-testid="group-header"
      aria-expanded={expanded}
      className="flex min-h-10 w-full items-center gap-2 bg-surface-raised py-1 pl-1 pr-3 sm:pl-1.5"
    >
      <Button
        variant="ghost"
        size="icon"
        className="size-6 shrink-0"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-label="Toggle group"
      >
        <ChevronRightIcon
          aria-hidden="true"
          className={cn(
            'size-4 text-content-secondary transition-transform duration-150',
            expanded ? 'rotate-90' : 'rotate-0'
          )}
        />
      </Button>
      {onTextClick ? (
        <button
          type="button"
          onClick={onTextClick}
          className="group/header flex min-w-0 items-center gap-2 rounded-sm border-0 bg-transparent p-0 text-left cursor-pointer focus-visible:outline-2 focus-visible:outline-border-focused"
        >
          {labelContent}
        </button>
      ) : (
        <div className="flex min-w-0 items-center gap-2">{labelContent}</div>
      )}
      <div className="flex-1" />
      {count !== undefined && (
        <Chip
          label={count}
          size="sm"
          className="hidden min-w-7 shrink-0 sm:inline-flex"
        />
      )}
      {secondaryText && <div className="ml-2 shrink-0">{secondaryText}</div>}
    </div>
  );
});
